import { StatusIndicatorProps } from "@amzn/awsui-components-react";
import { WorksheetStatus } from "src/models/multidimensional-allocation/MultiAllocationSheetData";
import { ExecutionStatus } from "src/models/multidimensional-allocation/ExecutionStatus";
import {ApprovalAction, ApprovalType} from "src/models/multidimensional-allocation/ProcessApprovals";
import {EntityType} from "src/models/multidimensional-allocation/EntityType";

// Centralized column definitions
export const COLUMN_DEFINITIONS = {
    WORKSHEET_NAME: {
        id: 'worksheetName',
        header: 'Worksheet Name',
        sortingField: 'worksheetName',
    },
    STATUS: {
        id: 'status',
        header: 'Status',
        sortingField: 'status',
    },
    CREATED_BY: {
        id: 'createdBy',
        header: 'Created By',
        sortingField: 'createdBy',
    },
    CREATED_ON: {
        id: 'createdOn',
        header: 'Created On',
        sortingField: 'createdOn',
    },
    LAST_UPDATED_BY: {
        id: 'lastUpdatedBy',
        header: 'Last Updated By',
        sortingField: 'lastUpdatedBy',
    },
    LAST_UPDATED_ON: {
        id: 'lastUpdatedOn',
        header: 'Last Updated On',
        sortingField: 'lastUpdatedOn',
    },
} as const;

// Centralized column definitions for allocation sheets
export const SHEET_COLUMN_DEFINITIONS = {
    NAME: {
        id: 'sheetName',
        header: 'Name',
        sortingField: 'sheetName',
    },
    TYPE: {
        id: 'type',
        header: 'Type',
        sortingField: 'type',
    },
    STATUS: {
        id: 'status',
        header: 'Status',
        sortingField: 'status',
    },
    SEGMENT_TYPE: {
        id: 'segmentType',
        header: 'Segment',
        sortingField: 'segmentType',
    },
    UPLOADED_AT: {
        id: 'lastUpdatedAt',
        header: 'Upload Date',
        sortingField: 'lastUpdatedAt',
    },
    UPLOADED_BY: {
        id: 'createdBy',
        header: 'Uploaded By',
        sortingField: 'createdBy',
    },
} as const;

// Extract column IDs for reuse
export const COLUMN_IDS = Object.values(COLUMN_DEFINITIONS).map(col => col.id);
export const SHEET_COLUMN_IDS = Object.values(SHEET_COLUMN_DEFINITIONS).map(col => col.id);

// Default preference for the MDA Sheet Table
export const DEFAULT_PREFERENCES = {
    pageSize: 25,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: [],
    stickyColumns: { first: 0, last: 0 },
    visibleColumns: []
};

// Default preferences for the worksheet table
export const DEFAULT_COLLECTION_PREFERENCES = {
    pageSize: 10,
    visibleColumns: COLUMN_IDS,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: COLUMN_IDS.map(id => ({ id, visible: true })),
    stickyColumns: { first: 0, last: 0 }
};

// Default preferences for the sheets table
export const SHEET_DEFAULT_COLLECTION_PREFERENCES = {
    pageSize: 10,
    visibleColumns: SHEET_COLUMN_IDS,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: SHEET_COLUMN_IDS.map(id => ({ id, visible: true })),
    stickyColumns: { first: 0, last: 0 }
};

// Page size options
export const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10 items' },
    { value: 25, label: '25 items' },
    { value: 50, label: '50 items' },
    { value: 100, label: "100 items" }
];

// Visible content options
export const VISIBLE_CONTENT_OPTIONS = Object.values(COLUMN_DEFINITIONS).map(col => ({
    id: col.id,
    label: col.header,
    editable: false,
}));

// Visible content options for sheets
export const SHEET_VISIBLE_CONTENT_OPTIONS = Object.values(SHEET_COLUMN_DEFINITIONS).map(col => ({
    id: col.id,
    label: col.header,
    editable: false,
}));

// Filtering properties for search
export const FILTERING_PROPERTIES = 
    [
        COLUMN_DEFINITIONS.WORKSHEET_NAME, 
        COLUMN_DEFINITIONS.STATUS, 
        COLUMN_DEFINITIONS.CREATED_BY, 
        COLUMN_DEFINITIONS.LAST_UPDATED_BY 
    ].map((column) => ({
        key: column.id,
        operators: [':', '!:', '=', '!='],
        propertyLabel: column.header,
        groupValuesLabel: `${column.header} values`
    }));

export const IMPACT_NOTICE_MESSAGES = new Map<ApprovalType, Map<ApprovalAction, string>>([
    [ApprovalType.MULTI_ALLOCATION_SHEET, new Map([
        [ApprovalAction.APPROVE, "Approving this allocation sheet will impact the linked worksheet(s). Once approved, all linked worksheets will go into draft status and also need to be reviewed and approved for the changes to take effect."],
        [ApprovalAction.REJECT, "Rejecting this allocation sheet will return it to Draft in Progress. You will need to resubmit it for review."]
    ])],
    [ApprovalType.MULTI_ALLOCATION_WORKSHEET, new Map([
        [ApprovalAction.APPROVE, "Approving this worksheet will impact the linked calculation(s). Once approved, all linked calculations will go into draft status and also need to be reviewed and approved for the changes to take effect."],
        [ApprovalAction.REJECT, "Rejecting this worksheet will return it to Draft in Progress. You will need to resubmit it for review."]
    ])],
    [ApprovalType.DERIVED_ALLOCATION_SHEET, new Map([
        [ApprovalAction.APPROVE, "Approving this derived allocation sheet makes the draft the live version, and the output of its last execution becomes the live allocation sheet."],
        [ApprovalAction.REJECT, "Rejecting this derived allocation sheet will return it to Draft in Progress. You will need to resubmit it for review."]
    ])]
]);

// Filtering properties for sheets
export const SHEET_FILTERING_PROPERTIES =
    [
        SHEET_COLUMN_DEFINITIONS.NAME,
        SHEET_COLUMN_DEFINITIONS.TYPE,
        SHEET_COLUMN_DEFINITIONS.STATUS,
        SHEET_COLUMN_DEFINITIONS.SEGMENT_TYPE,
        SHEET_COLUMN_DEFINITIONS.UPLOADED_BY
    ].map((column) => ({
        key: column.id,
        operators: [':', '!:', '=', '!='],
        propertyLabel: column.header,
        groupValuesLabel: `${column.header} values`
    }))

// Common Balance Pool (CBP) table config — MDA 1.1 Phase 2
export const CBP_COLUMN_DEFINITIONS = {
    NAME: { id: 'poolName', header: 'Name', sortingField: 'poolName' },
    STATUS: { id: 'status', header: 'Status', sortingField: 'status' },
    GRAIN: { id: 'grain', header: 'Grain', sortingField: 'grain' },
    CREATED_BY: { id: 'createdBy', header: 'Created by', sortingField: 'createdBy' },
    LAST_UPDATED_AT: { id: 'lastUpdatedAt', header: 'Last updated', sortingField: 'lastUpdatedAt' },
} as const;

export const CBP_COLUMN_IDS = Object.values(CBP_COLUMN_DEFINITIONS).map((col) => col.id);

// Name is the row header, so it stays visible; the rest are toggleable.
export const CBP_VISIBLE_CONTENT_OPTIONS = Object.values(CBP_COLUMN_DEFINITIONS).map((col) => ({
    id: col.id,
    label: col.header,
    editable: col.id !== CBP_COLUMN_DEFINITIONS.NAME.id,
}));

// Matches the shape of DEFAULT_COLLECTION_PREFERENCES (stripedRows / stickyColumns / visibleColumns included)
// so the CBP table preferences don't drift from the other MDA tables.
export const CBP_DEFAULT_COLLECTION_PREFERENCES = {
    // 10 per page to match the sibling MDA tabs (Allocation calculations / Manage allocation sheets), whose
    // DEFAULT_COLLECTION_PREFERENCES / SHEET_DEFAULT_COLLECTION_PREFERENCES also default to 10. Users can
    // still raise it via the table preferences (PAGE_SIZE_OPTIONS).
    pageSize: 10,
    visibleColumns: CBP_COLUMN_IDS,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: CBP_COLUMN_IDS.map((id) => ({ id, visible: true })),
    stickyColumns: { first: 0, last: 0 },
};

export const CBP_FILTERING_PROPERTIES =
    [
        CBP_COLUMN_DEFINITIONS.NAME,
        CBP_COLUMN_DEFINITIONS.STATUS,
        CBP_COLUMN_DEFINITIONS.GRAIN,
        CBP_COLUMN_DEFINITIONS.CREATED_BY,
    ].map((column) => ({
        key: column.id,
        operators: [':', '!:', '=', '!='],
        propertyLabel: column.header,
        groupValuesLabel: `${column.header} values`,
    }));


export const STATUS_INDICATOR_CONFIG: Record<WorksheetStatus, [string, StatusIndicatorProps.Type, StatusIndicatorProps.Color]> = 
{
 ["DRAFT_IN_PROGRESS"]: ["Draft In Progress", "in-progress", "grey"],
 ["PENDING_APPROVAL"]:["Pending Approval","pending", "yellow"],
 ["ACTIVE"]: ["Active","success", "green"],
 ["INACTIVE"]: ["Inactive","stopped", "red"]
}

export const EXECUTION_STATUS_INDICATOR_CONFIG: Record<ExecutionStatus, [string, StatusIndicatorProps.Type, StatusIndicatorProps.Color]> = 
{
 [ExecutionStatus.IN_PROGRESS]: ["In Progress", "loading", "grey"],
 [ExecutionStatus.COMPLETED]: ["Completed", "success", "green"],
 [ExecutionStatus.FAILED]: ["Failed", "error", "red"],
 [ExecutionStatus.PARTIALLY_FAILED]: ["Partially Failed", "warning", "yellow"]
}

export function getExecutionStatusConfig(status: string): [string, StatusIndicatorProps.Type, StatusIndicatorProps.Color | undefined] {
    return EXECUTION_STATUS_INDICATOR_CONFIG[status as ExecutionStatus] || ["Not Started", "stopped", "grey"];
}

export const getEntityLabel = (entityType: EntityType, linkedCount: number) => {
    if (entityType === EntityType.MULTI_ALLOCATION_WORKSHEET) {
        return linkedCount === 1 ? 'CLI' : 'CLIs';
    } else {
        return linkedCount === 1 ? 'Worksheet' : 'Worksheets';
    }
};
